//jsxhook
import React from "react"
import {
  BreadCrumbItem,
  BreadCrumbs,
  Label2,
  StandardXAxisPadding
} from "@sixty-six-north/ui-system"
import { useOrderState } from "cart/OrderService"
import {
  useCartLink,
  useCustomerInformationLink,
  useHomeLink,
  useOrderCompleteLink,
  usePaymentLink,
  useReviewLink
} from "components/Links"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import Link from "../../components/Link"
import { CheckoutSteps } from "../CheckoutSteps"

interface Props {
  currentStep: CheckoutSteps
}

interface LabelProps {
  label: React.ReactNode
  currentStep: CheckoutSteps
  step: CheckoutSteps
  as?: string
  href?: string
}

const BreadCrumbLabel: FC<LabelProps> = props => {
  if (props.step < props.currentStep && props.as && props.href)
    return (
      <Link as={props.as} href={props.href}>
        {props.label}
      </Link>
    )
  else {
    return (
      <Label2 sx={{ color: props.step === props.currentStep ? "blue.0" : "" }}>
        {props.label}
      </Label2>
    )
  }
}

const CheckoutCrumbs: FC<Props> = ({ currentStep }) => {
  const { t } = useTranslation("checkout")

  const order = useOrderState()
  const homeLink = useHomeLink()
  const orderCompleteLink = useOrderCompleteLink()
  const cartLink = useCartLink()
  const customerInformationLink = useCustomerInformationLink()
  const paymentLink = usePaymentLink()
  const reviewLink = useReviewLink()

  if (currentStep === CheckoutSteps.Confirmation && order) {
    return (
      <BreadCrumbs
        sx={{
          pl: StandardXAxisPadding,
          overflow: "auto"
        }}
      >
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("home", { defaultValue: "Home" })}
            currentStep={currentStep}
            step={CheckoutSteps.Home}
            {...homeLink}
          />
        </BreadCrumbItem>
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("orderComplete", { defaultValue: "Order Complete" })}
            currentStep={currentStep}
            step={CheckoutSteps.Confirmation}
            {...orderCompleteLink}
          />
        </BreadCrumbItem>
      </BreadCrumbs>
    )
  } else if (currentStep === CheckoutSteps.Confirmation) {
    return (
      <BreadCrumbs
        sx={{
          pl: StandardXAxisPadding,
          overflow: "auto"
        }}
      >
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("home", { defaultValue: "Home" })}
            currentStep={currentStep}
            step={CheckoutSteps.Home}
            {...homeLink}
          />
        </BreadCrumbItem>
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("orderComplete", { defaultValue: "Order Complete" })}
            currentStep={currentStep}
            step={CheckoutSteps.Confirmation}
          />
        </BreadCrumbItem>
      </BreadCrumbs>
    )
  } else {
    return (
      <BreadCrumbs
        sx={{
          pl: StandardXAxisPadding,
          overflow: "auto"
        }}
      >
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("bag", { defaultValue: "Bag" })}
            currentStep={currentStep}
            step={CheckoutSteps.Cart}
            {...cartLink}
          />
        </BreadCrumbItem>
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("shippingInformation", {
              defaultValue: "Shipping Information"
            })}
            currentStep={currentStep}
            step={CheckoutSteps.Information}
            {...customerInformationLink}
          />
        </BreadCrumbItem>
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("payment", { defaultValue: "Payment" })}
            currentStep={currentStep}
            step={CheckoutSteps.Payment}
            {...paymentLink}
          />
        </BreadCrumbItem>
        <BreadCrumbItem>
          <BreadCrumbLabel
            label={t("review", { defaultValue: "Review" })}
            currentStep={currentStep}
            step={CheckoutSteps.Review}
            {...reviewLink}
          />
        </BreadCrumbItem>
      </BreadCrumbs>
    )
  }
}

export default CheckoutCrumbs
